import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDateShort } from '../utils/date';
import { 
  H1, H2, Body, Card, Badge, Button, Spinner, 
  useToast 
} from '../components/ui';
import { Input, Select } from '../components/forms';
import { useModal } from '../stores/uiStore';
import { useAuth } from '../contexts/AuthContext';
import { useHasPermission } from '../stores/authStore';
import { 
  useObservations, 
  useDeleteObservation, 
  usePublishObservation, 
  useUnpublishObservation, 
  useVerifyObservation 
} from '../hooks/api';
import { ObservationFilters } from '../types/api';
import { CreateObservationModal, EditObservationModal, ObservationDetailModal, MediaGallery } from '../components/observations';

const Observations: React.FC = () => {
  const { user: _user } = useAuth();
  const _confirmModal = useModal('confirm-delete');
  const createModal = useModal('create-observation');
  const editModal = useModal('edit-observation');
  const detailModal = useModal('observation-detail');
  const { addToast: _addToast } = useToast();
  
  // Permissions
  const canCreateObservation = useHasPermission('observation.create');
  const canEditObservation = useHasPermission('observation.update');
  const canDeleteObservation = useHasPermission('observation.delete');
  const canPublishObservation = useHasPermission('observation.publish');
  const canVerifyObservation = useHasPermission('observation.verify');
  
  // Filters state. Use `updateFilters` to mutate so that any filter
  // change automatically resets `skip` to 0 -- otherwise switching
  // category while on page 3 leaves the offset stale against a smaller
  // result set and silently shows an empty page.
  const [filters, setFilters] = useState<ObservationFilters>({
    limit: 20,
    skip: 0,
  });
  const updateFilters = (patch: Partial<ObservationFilters>) =>
    setFilters(prev => ({ ...prev, ...patch, skip: 0 }));
  
  // API hooks
  const { data, isLoading, error, refetch } = useObservations(filters);
  const deleteMutation = useDeleteObservation();
  const publishMutation = usePublishObservation();
  const unpublishMutation = useUnpublishObservation();
  const verifyMutation = useVerifyObservation();
  
  // Handle actions
  const handleDeleteObservation = (observationId: string) => {
    // For now, we'll use a simple confirm dialog
    const confirmed = window.confirm('Are you sure you want to delete this observation? This action cannot be undone.');
    if (confirmed) {
      deleteMutation.mutate(observationId);
    }
  };
  
  const handlePublishObservation = (observationId: string) => {
    publishMutation.mutate(observationId);
  };
  
  const handleUnpublishObservation = (observationId: string) => {
    unpublishMutation.mutate(observationId);
  };
  
  const handleVerifyObservation = (observationId: string) => {
    verifyMutation.mutate(observationId);
  };
  
  const handleCreateObservation = () => {
    createModal.open();
  };
  
  const handleEditObservation = (observationId: string) => {
    editModal.open({ observationId });
  };
  
  const handleViewObservation = (observationId: string) => {
    detailModal.open({ observationId });
  };

  // Deep-link support: opening /observations?id=<id> auto-shows the
  // detail modal. Powers the dashboard's clickable recent-observations
  // rows and any future inbound links from notifications, reports, etc.
  // We leave the param in the URL on purpose -- stripping it via
  // setSearchParams races with the modal-open call and closes the
  // modal before it mounts; cleaner to keep the deep link addressable.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      detailModal.open({ observationId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot on mount
  }, []);

  // Get category variant
  const getCategoryVariant = (category: string) => {
    switch (category) {
      case 'academic': return 'info';
      case 'social': return 'success';
      case 'emotional': return 'warning';
      case 'physical': return 'secondary';
      default: return 'default';
    }
  };
  
  // formatDate is imported from utils/date
  
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <H2 className="text-red-600 mb-2">Error Loading Observations</H2>
          <Body className="text-gray-600 dark:text-gray-400 mb-4">
            Unable to load observations. Please try again.
          </Body>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }
  
  const observations = data?.items || [];
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <H1 className="mb-2">Observations</H1>
          <Body className="text-gray-600 dark:text-gray-400">
            Track and manage child observations.
          </Body>
        </div>
        {canCreateObservation && (
          <Button variant="primary" onClick={handleCreateObservation} className="self-start sm:self-auto">
            Add Observation
          </Button>
        )}
      </div>
      
      {/* Filters */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <Select
                value={filters.category || ''}
                onChange={(e) => updateFilters({ category: e.target.value || undefined })}
                options={[
                  { value: '', label: 'All Categories' },
                  { value: 'general', label: 'General' },
                  { value: 'academic', label: 'Academic' },
                  { value: 'social', label: 'Social' },
                  { value: 'emotional', label: 'Emotional' },
                  { value: 'physical', label: 'Physical' },
                ]}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => updateFilters({ date_from: e.target.value || undefined })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <Input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => updateFilters({ date_to: e.target.value || undefined })}
              />
            </div>
            
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={() => setFilters({ limit: 20, skip: 0 })}
                data-testid="observations-clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Observations List */}
      {observations.length === 0 ? (
        <Card>
          <div className="p-8 text-center">
            <H2 className="text-gray-500 dark:text-gray-400 mb-2">No Observations Found</H2>
            <Body className="text-gray-400 dark:text-gray-500 mb-4">
              No observations match your current filters.
            </Body>
            {canCreateObservation && (
              <Button variant="primary" onClick={handleCreateObservation}>
                Create First Observation
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {observations.map((observation) => (
            <Card key={observation.id}>
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <div>
                    <H2 className="mb-1">
                      {observation.child_name || 'Unknown Child'}
                    </H2>
                    <Body className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDateShort(observation.observation_date)}
                      {observation.teacher_name && (
                        <span className="ml-2">
                          by {observation.teacher_name}
                        </span>
                      )}
                    </Body>
                  </div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    <Badge
                      variant={getCategoryVariant(observation.category)}
                      size="sm"
                    >
                      {observation.category}
                    </Badge>

                    {observation.is_milestone && (
                      <Badge variant="success" size="sm">
                        Milestone
                      </Badge>
                    )}

                    {observation.is_concern && (
                      <Badge variant="error" size="sm">
                        Concern
                      </Badge>
                    )}

                    {observation.follow_up_required && (
                      <Badge variant="warning" size="sm">
                        Follow-up
                      </Badge>
                    )}

                    <Badge
                      variant={observation.is_published ? 'success' : 'default'}
                      size="sm"
                    >
                      {observation.is_published ? 'Published' : 'Draft'}
                    </Badge>

                    {observation.is_verified && (
                      <Badge variant="info" size="sm">
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>
                
                <Body className="text-gray-700 dark:text-gray-300 mb-3">
                  {observation.narrative}
                </Body>

                {observation.learning_area && (
                  <Body className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Learning Area: {observation.learning_area}
                  </Body>
                )}
                
                {/* Media Gallery */}
                {observation.media && observation.media.length > 0 && (
                  <div className="mb-4">
                    <MediaGallery
                      media={observation.media}
                      maxItemsToShow={3}
                      size="sm"
                      showCaptions={false}
                    />
                  </div>
                )}
                
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-2">
                    {canEditObservation && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditObservation(observation.id)}
                      >
                        Edit
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewObservation(observation.id)}
                    >
                      View Details
                    </Button>

                    {canPublishObservation && !observation.is_published && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handlePublishObservation(observation.id)}
                        disabled={publishMutation.isPending}
                      >
                        Publish
                      </Button>
                    )}

                    {canPublishObservation && observation.is_published && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnpublishObservation(observation.id)}
                        disabled={unpublishMutation.isPending}
                      >
                        Unpublish
                      </Button>
                    )}

                    {canVerifyObservation && !observation.is_verified && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleVerifyObservation(observation.id)}
                        disabled={verifyMutation.isPending}
                      >
                        Verify
                      </Button>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    {canDeleteObservation && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteObservation(observation.id)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex flex-col items-center gap-2 mt-8 sm:flex-row sm:justify-center sm:gap-4">
          <span
            className="text-sm text-gray-600 dark:text-gray-400"
            data-testid="observations-pager-counter"
          >
            Showing {(data.page - 1) * (filters.limit ?? 20) + 1}
            -{Math.min((data.page - 1) * (filters.limit ?? 20) + data.size, data.total)}
            {' '}of {data.total}
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              data-testid="observations-pager-prev"
              disabled={data.page === 1}
              onClick={() => setFilters(prev => ({
                ...prev,
                skip: Math.max(0, (prev.skip || 0) - (prev.limit || 20)),
              }))}
            >
              Previous
            </Button>

            <span className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
              Page {data.page} of {data.pages}
            </span>

            <Button
              variant="secondary"
              data-testid="observations-pager-next"
              disabled={data.page >= data.pages}
              onClick={() => setFilters(prev => ({
                ...prev,
                skip: (prev.skip || 0) + (prev.limit || 20),
              }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      {/* Modals */}
      <CreateObservationModal />
      <EditObservationModal />
      <ObservationDetailModal
        onEdit={handleEditObservation}
        onDelete={handleDeleteObservation}
        onPublish={handlePublishObservation}
        onUnpublish={handleUnpublishObservation}
        onVerify={handleVerifyObservation}
      />
    </div>
  );
};

export default Observations;
